import React, {
    useContext,
    useEffect,
    useState
} from 'react';
import { useQuery } from '@tanstack/react-query';

import config from "../config/config";
import * as QUERIES from "../queries/query-functions";
import * as UTILS from "../utils/utils";

import LoadingIndicator from '../components/loading-indicator';
import AddToCalendarButton from '../components/add-to-calendar-button';
import OptionsImage from '../components/options-image';

const order_confirmation_data = config.OPTION_FIELDS.order_confirmation;

export default function OrderConfirmation(props) {

    const [latestOrder, setLatestOrder] = useState(null);

    const { 
        isFetching: ordersFetching,
        isPending: ordersPending,
        data: ordersData,
    } = useQuery({
        queryKey: ['ordersData'],
        queryFn: QUERIES.getOrders,
        refetchOnWindowFocus: false,
        retry: false
    });

    useEffect(() => {
        if (!ordersData) return;

        const latest_order = UTILS.findClosestDate(ordersData, "firstTransactionDate");
        setLatestOrder(latest_order);
    }, [ordersData])

    useEffect(() => {
    if (!latestOrder) return;

    const transformToSchema = (raw) => {
        const transactionId = raw.id;
        const transactionTotal = raw.total;

        const transactionProducts = [];
        const ecommerceItems = [];

        // Tickets
        const transactionTickets = (raw.tickets || []).map(ticket => {
            const productType = 'T';
            const eventId = (ticket.instance.event.id).match(/\d+/g).join('');
            const instanceId = (ticket.instance.id).match(/\d+/g).join('');
            const ticketTypeId = (ticket.ticketType.id).match(/\d+/g).join('');
            const price = ticket.price > 0 ? ticket.price.toFixed(2).replace('.', '') : '0';
            const offerId = ticket.offer?.id ? (ticket.offer.id.match(/\d+/g)?.join('') || '') : '';

            const itemId = offerId
            ? `${productType}${eventId}I${instanceId}-${ticketTypeId}P${price}-O${offerId}`
            : `${productType}${eventId}I${instanceId}-${ticketTypeId}P${price}`;

            return {
                item_id: itemId,
                item_name: ticket.instance.event.name,
                item_category: 'Tickets',
                item_category3: ticket.ticketType.name,
                item_variant: new Date(ticket.instance.start).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                }),
                price: ticket.price,
                discount: ticket.discount || 0,
                quantity: 1
            };
        });

        // Deduplicate tickets by item_id
        const uniqueTickets = transactionTickets.reduce((acc, current) => {
            const existing = acc.find(item => item.item_id === current.item_id);
            if (existing) {
                existing.quantity += current.quantity;
            } else {
                acc.push(current);
            }
            return acc;
        }, []);

        // Add tickets to outputs
        uniqueTickets.forEach(ticket => {
            transactionProducts.push({
                sku: ticket.item_id,
                name: ticket.item_name,
                category: ticket.item_category,
                price: ticket.price,
                quantity: ticket.quantity
            });
            ecommerceItems.push(ticket);
        });

        // Merchandise
        (raw.merchandiseItems || []).forEach(item => {
            transactionProducts.push({
                sku: item.id,
                name: item.name,
                category: 'Merchandise',
                price: item.price,
                quantity: item.quantity
            });

            ecommerceItems.push({
                item_id: item.id,
                item_name: item.name,
                item_category: 'Merchandise',
                price: item.price,
                quantity: item.quantity,
                discount: item.discount || 0
            });
        });

        // Donations
        (raw.donations || []).forEach(donation => {

            const fundId = (donation.fund.id).match(/\d+/g).join('');
            const itemId = `D${fundId}`; 

            transactionProducts.push({
                sku: itemId,
                name: donation.name,
                category: 'Donations',
                price: donation.amount,
                quantity: 1
            });

            ecommerceItems.push({
                item_id: itemId,
                item_name: donation.name,
                item_category: 'Donations',
                price: donation.amount,
                quantity: 1,
                discount: 0
            });
        });

        // Gift Vouchers
        (raw.giftVouchers || []).forEach(gv => {
            transactionProducts.push({
                sku: "G",
                name: gv.name,
                category: 'Gift Vouchers',
                price: gv.value,
                quantity: 1
            });

            ecommerceItems.push({
                item_id: "G",
                item_name: gv.name,
                item_category: 'Gift Vouchers',
                price: gv.value,
                quantity: 1,
                discount: 0
            });
        });

        // Fees
        if (raw.totalDeliveryCharges > 0) {
            transactionProducts.push({
                sku: 'F-Delivery',
                name: 'Delivery Fee',
                category: 'Fees',
                price: raw.totalDeliveryCharges,
                quantity: 1
            });
            ecommerceItems.push({
                item_id: 'F-Delivery',
                item_name: 'Delivery Fee',
                item_category: 'Fees',
                price: raw.totalDeliveryCharges,
                quantity: 1,
                discount: 0
            });
        }

        if (raw.totalTransactionCharges > 0) {
            transactionProducts.push({
                sku: 'F-Transaction',
                name: 'Transaction Fee',
                category: 'Fees',
                price: raw.totalTransactionCharges,
                quantity: 1
            });
            ecommerceItems.push({
                item_id: 'F-Transaction',
                item_name: 'Transaction Fee',
                item_category: 'Fees',
                price: raw.totalTransactionCharges,
                quantity: 1,
                discount: 0
            });
        }

        return {
            transactionId,
            transactionTotal,
            transactionShipping: raw.totalDeliveryCharges || 0,
            transactionCity: null,
            transactionState: null,
            transactionCountry: null,
            transactionProducts,
            ecommerce: {
            transaction_id: transactionId,
            value: transactionTotal,
            shipping: raw.totalDeliveryCharges || 0,
            items: ecommerceItems
            },
            event: 'Spektrix Transaction'
        };
    }

    window.dataLayer = window.dataLayer || [];
    const schema = transformToSchema(latestOrder);

    //pushing just GA4 ecommerce data as opposed to old UA Transaction data as well 
    window.dataLayer.push(schema);
    
    }, [latestOrder]);

    return (
        <div className='booking-pathway__container booking-pathway__order-confirmation'>

            <div className='booking-pathway__main-content'>
                <h1 className='sr-only'>Your order is complete.</h1>
                {order_confirmation_data.introduction &&
                    <div className='booking-pathway__header' dangerouslySetInnerHTML={{__html: order_confirmation_data.introduction }}></div>
                }
                {order_confirmation_data.button?.url &&
                    <div className='booking-pathway__order-confirmation--button'>
                        <a href={order_confirmation_data.button.url} className='btn'>{ order_confirmation_data.button.title ? order_confirmation_data.button.title : 'Find out more' }</a>
                    </div>
                }
                {order_confirmation_data.images &&
                    <div className='booking-pathway__order-confirmation--images'>
                        {order_confirmation_data.images.map((image, index) => (
                            <OptionsImage key={index} image={image} />
                        ))}
                    </div>
                }
            </div>

            <div className='booking-pathway__summary'>
                {ordersPending || ordersFetching || latestOrder == null ?
                    <LoadingIndicator />
                : latestOrder ? 
                (
                    <>
                        {latestOrder.donations?.map((donation) => (
                            <div key={donation.id} className='confirmed-order confirmed-order__donation'>
                                <h2 className='confirmed-order__header'>{donation.fund.name}</h2>
                                <p>{`Thank you for your donation for £${donation.amount.toFixed(2)}.`}</p>
                            </div>
                        ))}
                
                        {latestOrder.tickets?.map((ticket) => {
                            const formattedDate = UTILS.formatDateTime(ticket.instance.start);
                            return (
                                <div key={ticket.id} className='confirmed-order confirmed-order__ticket'>
                                    <h2 className='confirmed-order__header'>{ticket.instance.event.name}</h2>
                                    <p className="instance-event-summary__date">{formattedDate}</p>
                                    <p className="instance-event-summary__venue">{ticket.instance.event[`attribute_${config.OPTION_FIELDS.venue_attribute_name}`]}</p>
                                    <AddToCalendarButton ticket={ticket}  />
                                </div>
                            )
                        })}
                
                        {latestOrder.membershipSubscription?.map((membership) => (
                                <div key={membership.id} className='confirmed-order confirmed-order__membership'>
                                    <h2 className='confirmed-order__header'>{membership.membership.name}</h2>
                                    <p>Thank you for joining us.</p>
                                </div>
                        ))}
                    </>
                )
                    : null
                }

            </div>
        </div>
    );
}